import { getCurrentUser } from './auth';
import { effectivePermissions } from './permissionCatalog';

/**
 * Check if the current logged-in admin user has a specific permission.
 *
 * - superadmin users ALWAYS return true (full access).
 * - Admin users with role "Super Admin" ALWAYS return true.
 * - For other admin users, checks the permissions array from login data.
 * - Non-admin users (student, instructor) return false.
 *
 * @param {string} permissionKey - e.g. 'view_students', 'manage_batches'
 * @returns {boolean}
 */
export function hasPermission(permissionKey) {
  const user = getCurrentUser();
  if (!user) return false;

  // tenantadmin (platform owner) = full access
  if (user.usertype === 'tenantadmin') return true;

  // superadmin usertype (institute-level) = full access within institute
  if (user.usertype === 'superadmin') return true;

  // Only admin users have granular permissions
  if (user.usertype !== 'admin') return false;

  // Admin with "Super Admin" role = full access
  if (user.adminRole === 'Super Admin') return true;

  // What the admin holds — for one saved before the newer keys, those too
  // (utils/permissionCatalog, the server's rule).
  return effectivePermissions(user).includes(permissionKey);
}

/**
 * Check if the current user has ANY of the given permissions.
 * @param  {...string} permissionKeys
 * @returns {boolean}
 */
export function hasAnyPermission(...permissionKeys) {
  return permissionKeys.some(key => hasPermission(key));
}

/**
 * Check if the current user has ALL of the given permissions.
 * @param  {...string} permissionKeys
 * @returns {boolean}
 */
export function hasAllPermissions(...permissionKeys) {
  return permissionKeys.every(key => hasPermission(key));
}

/**
 * Check if the current user is the Institute Super Admin tier — i.e.
 * the top admin of the institute, not a regular sub-admin/moderator.
 *
 * Returns true for:
 *  - usertype === 'tenantadmin' (platform owner — outranks institute SA)
 *  - usertype === 'superadmin'  (legacy institute super admin)
 *  - usertype === 'admin' AND Admin.role === 'Super Admin'
 *
 * Use this to gate settings/actions that even a permissioned sub-admin
 * must NEVER see (e.g. enquiry-ownership policy, billing controls).
 */
export function isInstituteSuperAdmin() {
  const user = getCurrentUser();
  if (!user) return false;
  if (user.usertype === 'tenantadmin') return true;
  if (user.usertype === 'superadmin') return true;
  if (user.usertype === 'admin' && user.adminRole === 'Super Admin') return true;
  return false;
}
